import { Placement } from 'common/models/Placements';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ConfirmationDialog from '../Generic/ConfirmationDialog';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { BulkRefreshPlacementToAstutePayroll } from 'services/PlacementsService';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import { Link } from 'react-router-dom';
import ListItemText from '@mui/material/ListItemText';

type PlacementRefreshData = Pick<
  Placement,
  'id' | 'exportedToBackOffice' | 'approved' | 'placementTypeID' | 'jobTitle' | 'placementTypeName'
>;

type ListType = null | 'total' | 'invalid' | 'valid';

interface Props {
  open: boolean;
  placements: PlacementRefreshData[];
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

const listSpanStyle: React.CSSProperties = { cursor: 'pointer', textDecoration: 'underline' };
const listItemLinkStyle: React.CSSProperties = {
  width: '100%',
  color: 'inherit',
  textDecoration: 'none',
  padding: '0 16px',
};

export default function RefreshPlacementsToAstuteDialog({
  open,
  placements,
  closeHandler,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [listType, setListType] = useState<ListType>(null);
  const [notApprovedOrExported, setNotApprovedOrExported] = useState<PlacementRefreshData[]>([]);
  const [valids, setValids] = useState<PlacementRefreshData[]>([]);

  useEffect(() => {
    if (!open) return;
    const notValidsTmp: PlacementRefreshData[] = [];
    const validsTmp: PlacementRefreshData[] = [];

    for (let i = 0; i < placements.length; i++) {
      const p = placements[i];
      if (!p.approved || !p.exportedToBackOffice || p.placementTypeID !== 2) {
        notValidsTmp.push(p);
        continue;
      }
      validsTmp.push(p);
    }
    setNotApprovedOrExported(notValidsTmp);
    setValids(validsTmp);
  }, [placements]);

  const allValids = useMemo(() => {
    return valids.length === placements.length;
  }, [valids.length, placements.length]);

  const submitCallback = useCallback(async () => {
    loadingHandler?.(true);
    const ids = valids.map((p) => p.id);
    const res = await BulkRefreshPlacementToAstutePayroll(ids, errorHandler);
    if (res) {
      successHandler?.(`Successfully refreshed ${ids.length} placement(s) to Astute`);
      closeHandler();
    }
    loadingHandler?.(false);
  }, [valids, closeHandler, loadingHandler, errorHandler, successHandler]);

  const listItems = useMemo(() => {
    if (listType === null) return [];
    if (listType === 'total') return placements;
    if (listType === 'invalid') return notApprovedOrExported;
    if (listType === 'valid') return valids;
    return [];
  }, [listType, placements, notApprovedOrExported, valids]);

  const listTitle = useMemo(() => {
    if (listType === 'total') return 'Total Selected';
    if (listType === 'invalid') return 'Not yet approved or exported';
    if (listType === 'valid') return 'Valid Records';
    return '';
  }, [listType]);

  if (allValids) {
    return (
      <ConfirmationDialog
        open={open}
        message="Are you sure you want to Refresh data to Astute?"
        onClose={closeHandler}
        onContinue={submitCallback}
        title="Confirm Action"
        confirmActionText="Yes"
        cancelActionText="No"
      />
    );
  }

  return (
    <>
      <Dialog open={open && listType !== null && listItems.length > 0} fullWidth>
        <DialogTitle>{listTitle}</DialogTitle>
        <DialogContent>
          <List>
            {listItems.map((p, i) => (
              <ListItemButton key={i} disableGutters>
                <Link to={`/placements/${p.id}`} target="_blank" style={listItemLinkStyle}>
                  <ListItemText
                    primary={`${p.id} - ${p.jobTitle}`}
                    secondary={p.placementTypeName}
                  />
                </Link>
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setListType(null)}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open} fullWidth>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          {placements.length > 0 && (
            <div>
              Total Selected -{' '}
              <span style={listSpanStyle} onClick={() => setListType('total')}>
                {placements.length}
              </span>
            </div>
          )}
          {notApprovedOrExported.length > 0 && (
            <div>
              Not yet approved or exported -{' '}
              <span style={listSpanStyle} onClick={() => setListType('invalid')}>
                {notApprovedOrExported.length}
              </span>
            </div>
          )}
          {valids.length > 0 && (
            <div>
              Valid records -{' '}
              <span style={listSpanStyle} onClick={() => setListType('valid')}>
                {valids.length}
              </span>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={closeHandler}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={valids.length === 0}
            onClick={submitCallback}
            color="success"
          >
            Proceed with valid records [ {valids.length} ]
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
